/**
 * Support desk and licence renewals — shared contracts.
 *
 * Three audiences share these shapes — a business's own staff, the account's
 * contact person, and the Owner — so they live together rather than being
 * split by caller. What differs between the callers is authorisation and which
 * rows come back, never the vocabulary.
 */
import { z } from 'zod';
import { idSchema, paginationSchema } from './common.js';
/* ------------------------------ vocabulary ------------------------------ */
export const TICKET_STATUSES = ['open', 'in_progress', 'escalated', 'resolved', 'closed'];
export const TICKET_PRIORITIES = ['low', 'normal', 'high', 'urgent'];
/** The three audiences that can write on a ticket. */
export const TICKET_AUTHOR_KINDS = ['business_user', 'contact_person', 'owner'];
export const RENEWAL_STATUSES = ['pending', 'approved', 'rejected'];
export const DESK_ATTACHMENT_OWNERS = ['ticket', 'ticket_message', 'renewal_request'];
export const MAX_DESK_ATTACHMENTS = 5;
/**
 * Ids of files already uploaded through the desk's own upload endpoint.
 * Two steps rather than one multipart submit: the screenshot is in place
 * before the ticket is written, so a slow upload never costs somebody the
 * description they just typed.
 */
export const deskAttachmentIdsSchema = z.array(idSchema).max(MAX_DESK_ATTACHMENTS).default([]);
/* -------------------------------- tickets ------------------------------- */
export const createTicketSchema = z.object({
    subject: z.string().trim().min(5).max(200),
    body: z.string().trim().min(10).max(5000),
    priority: z.enum(TICKET_PRIORITIES).default('normal'),
    attachmentIds: deskAttachmentIdsSchema,
});
/** Same, plus the business it is about — the portal serves several. */
export const createTicketForBusinessSchema = createTicketSchema.extend({
    businessId: idSchema,
});
export const ticketMessageSchema = z.object({
    body: z.string().trim().min(1).max(5000),
    attachmentIds: deskAttachmentIdsSchema,
    /**
     * Kept from the business. Only the contact person and the Owner may set it;
     * the business-side endpoint ignores it outright.
     */
    isInternal: z.boolean().default(false),
});
export const resolveTicketSchema = z.object({ note: z.string().trim().min(5).max(2000) });
export const escalateTicketSchema = z.object({
    note: z.string().trim().min(5).max(2000),
    priority: z.enum(TICKET_PRIORITIES).optional(),
});
export const setTicketStatusSchema = z.object({
    status: z.enum(TICKET_STATUSES),
    note: z.string().trim().max(2000).optional(),
});
export const ticketsListSchema = paginationSchema.extend({
    businessId: idSchema.optional(),
    status: z.enum(TICKET_STATUSES).optional(),
    /** Owner shortcut: only what a contact person handed up. */
    escalatedOnly: z.coerce.boolean().optional(),
    q: z.string().trim().max(120).optional(),
});
/* ------------------------------- renewals ------------------------------- */
export const createRenewalSchema = z.object({
    businessId: idSchema,
    /** Paisa, so no rounding happens in a browser. */
    amountPaisa: z.number().int().positive().max(2_000_000_000),
    reference: z.string().trim().max(120).optional(),
    note: z.string().trim().max(2000).optional(),
    /** The transfer screenshot. Required — it is the whole point of the request. */
    attachmentIds: z.array(idSchema).min(1).max(MAX_DESK_ATTACHMENTS),
});
export const renewalsListSchema = paginationSchema.extend({
    businessId: idSchema.optional(),
    status: z.enum(RENEWAL_STATUSES).optional(),
});
export const approveRenewalSchema = z.object({
    /** The date access runs to, chosen by whoever checked the money. */
    approvedUntil: z.string().datetime(),
    note: z.string().trim().max(2000).optional(),
});
export const rejectRenewalSchema = z.object({ note: z.string().trim().min(5).max(2000) });
/* --------------------------- platform accounts -------------------------- */
/** CNIC as printed, with or without dashes; stored in the printed form. */
export const deskCnicSchema = z
    .string()
    .trim()
    .regex(/^\d{5}-?\d{7}-?\d$/, 'CNIC must be 13 digits')
    .transform((v) => {
    const digits = v.replace(/-/g, '');
    return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;
});
export const createPlatformUserSchema = z.object({
    fullName: z.string().trim().min(2).max(120),
    fatherName: z.string().trim().max(120).optional(),
    cnic: deskCnicSchema.optional(),
    email: z.string().trim().toLowerCase().email().max(254),
    phone: z.string().trim().max(20).optional(),
    /** Handed over directly; the portal forces a change on first sign-in. */
    password: z.string().min(12).max(200),
});
export const updatePlatformUserSchema = z.object({
    fullName: z.string().trim().min(2).max(120).optional(),
    fatherName: z.string().trim().max(120).nullable().optional(),
    cnic: deskCnicSchema.nullable().optional(),
    phone: z.string().trim().max(20).nullable().optional(),
    isActive: z.boolean().optional(),
});
export const resetPlatformUserPasswordSchema = z.object({ password: z.string().min(12).max(200) });
export const assignContactSchema = z.object({
    /** Null clears the assignment and leaves the business without a contact. */
    contactUserId: idSchema.nullable(),
});
/* ---------------------------- client portal ----------------------------- */
export const clientLoginSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(254),
    password: z.string().min(1).max(200),
});
export const clientChangePasswordSchema = z.object({
    currentPassword: z.string().min(1).max(200),
    newPassword: z.string().min(12).max(200),
});
//# sourceMappingURL=support-desk.js.map